import { useState, useEffect, useCallback } from 'react'
import { router } from '@inertiajs/react'
import { X, Search, CirclePlus, Ship } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { useOrgPath } from '@/lib/pathFor'

interface LinkShipmentModalProps {
  open: boolean
  onClose: () => void
  emailThreadId: string | null
  linkedShipmentIds?: string[]
  onCreateShipment?: () => void
  filter?: string
  attention?: string | null
  search?: string
  cursor?: string | null
}

interface SearchResult {
  id: string
  internal_reference: string
  origin_port: string | null
  destination_port: string | null
  client_name: string | null
  earliest_etd: string | null
  container_summary: string | null
  booking_count: number
  updated_at: string
  matched_references: Array<{ field: string; label: string; value: string }>
}

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return '--'
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function LinkShipmentModal({ open, onClose, emailThreadId, linkedShipmentIds = [], onCreateShipment, filter, attention, search, cursor }: LinkShipmentModalProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const pathFor = useOrgPath()

  const fetchShipments = useCallback(async (q: string) => {
    setLoading(true)
    try {
      const resp = await fetch(pathFor(`/shipments/search.json?q=${encodeURIComponent(q)}`))
      if (resp.ok) {
        const data = await resp.json()
        setResults(data)
        if (data.length > 0 && !selectedId) {
          setSelectedId(data[0].id)
        }
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false)
    }
  }, [pathFor, selectedId])

  useEffect(() => {
    if (!open) return
    const timer = setTimeout(() => fetchShipments(searchQuery), 300)
    return () => clearTimeout(timer)
  }, [searchQuery, open, fetchShipments])

  useEffect(() => {
    if (open) {
      setSearchQuery('')
      setSelectedId(null)
      setResults([])
      fetchShipments('')
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null

  async function handleAssign() {
    if (!selectedId || !emailThreadId) return

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const resp = await fetch(pathFor('/thread_shipments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ shipment_id: selectedId, email_thread_id: emailThreadId }),
      })

      if (resp.ok) {
        toast.success('Shipment linked to thread')
        onClose()
        // C3: Preserve filter/search/cursor context across inbox actions
        const params = new URLSearchParams()
        if (filter) params.set('filter', filter)
        if (attention) params.set('attention', attention)
        if (search) params.set('search', search)
        if (cursor) params.set('cursor', cursor)
        const query = params.toString()
        const url = window.location.pathname + (query ? `?${query}` : '')
        router.visit(url, { preserveScroll: true })
      } else {
        const data = await resp.json().catch(() => ({}))
        toast.error(data.error || 'Failed to link shipment')
      }
    } catch {
      toast.error('Failed to link shipment')
    }
  }

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={handleOverlayClick}
    >
      <div
        className="flex flex-col bg-white rounded-xl shadow-lg"
        style={{ width: 500, height: 540, maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <h2 className="text-base font-semibold text-foreground font-space-grotesk">
            Link to Shipment
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center h-7 w-7 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 px-5 pb-4 flex flex-col gap-3">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search Internal Ref, booking ID, client, route, document..."
              className="w-full h-10 rounded-lg border border-input pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
            />
          </div>

          {/* Results list */}
          <div className="flex-1 min-h-[280px] rounded-lg border overflow-y-auto">
            {results.map((shipment) => {
              const isLinked = linkedShipmentIds.includes(shipment.id)
              return (
                <div
                  key={shipment.id}
                  onClick={() => !isLinked && setSelectedId(shipment.id)}
                  className={`px-4 py-3 border-b last:border-b-0 transition-colors flex flex-col gap-1 ${
                    isLinked ? 'opacity-50' : 'cursor-pointer'
                  } ${
                    selectedId === shipment.id && !isLinked ? 'bg-blue-50' : 'hover:bg-muted/50'
                  }`}
                >
                  {/* Primary line: Internal Ref + route + container summary */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Ship className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <div>
                        <span className={`block font-mono text-[13px] text-foreground ${selectedId === shipment.id ? 'font-semibold' : 'font-medium'}`}>
                          {shipment.internal_reference}
                        </span>
                        <span className="block text-[11px] text-muted-foreground">
                          {shipment.origin_port || '--'} → {shipment.destination_port || '--'}
                        </span>
                      </div>
                      {shipment.container_summary && (
                        <span className="text-[13px] text-muted-foreground">
                          ({shipment.container_summary})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {isLinked && (
                        <Badge variant="outline" size="xs" className="text-muted-foreground">
                          Linked
                        </Badge>
                      )}
                      {shipment.booking_count > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {shipment.booking_count} booking{shipment.booking_count !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Secondary line: client + earliest ETD */}
                  <p className="text-xs text-muted-foreground ml-[22px]">
                    {shipment.client_name || 'No client'} · Earliest ETD {formatShortDate(shipment.earliest_etd)}
                  </p>
                  {shipment.matched_references.length > 0 && (
                    <div className="ml-[22px] flex flex-wrap gap-1">
                      {shipment.matched_references.slice(0, 3).map((match) => (
                        <Badge key={`${match.field}-${match.value}`} variant="outline" size="xs" className="font-normal">
                          {match.label}: {match.value}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}

            {results.length === 0 && !loading && (
              <div className="px-4 py-4 text-center text-xs text-muted-foreground">
                No shipments found. Try a different search or create a new shipment.
              </div>
            )}

            {loading && (
              <div className="px-4 py-8 flex flex-col gap-3">
                <div className="h-4 bg-muted rounded animate-pulse" />
                <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                <div className="h-4 bg-muted rounded animate-pulse w-1/2" />
              </div>
            )}

            {/* Create new shipment row */}
            <div
              onClick={() => {
                onClose()
                onCreateShipment?.()
              }}
              className="px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 flex items-center gap-2"
            >
              <CirclePlus className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
              <span className="text-[13px] font-medium text-blue-600">
                Create new shipment from this email
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleAssign}
            className="cursor-pointer bg-primary"
            disabled={!selectedId || !emailThreadId}
          >
            Link
          </Button>
        </div>
      </div>
    </div>
  )
}
